import cx from "classnames";
import { ChangeEvent, DragEvent, forwardRef, useId, useState } from "react";

import { CloudUpload, DropFile } from "components/icons/generated";
import Icon from "ds/components/Icon";
import Typography from "ds/components/Typography";
import { withTestId } from "utils/withTestId";
import { pluralize } from "shared/Pluralize";

import Box from "../../Box";
import { FILE_INPUT_TEST_ID } from "../constants";
import styles from "./styles.module.css";

type FileUploadDragDropAreaBaseProps = {
  ariaLabel?: string;
  name?: string;
  caption?: string;
  className?: string;
  textAlign?: "left" | "center";
};

type FileUploadDragDropAreaSingleProps = FileUploadDragDropAreaBaseProps & {
  multiple?: false;
  onChange: (file: File) => void;
};

type FileUploadDragDropAreaMultipleProps = FileUploadDragDropAreaBaseProps & {
  multiple: true;
  onChange: (files: File[]) => void;
};

type FileUploadDragDropAreaProps =
  | FileUploadDragDropAreaSingleProps
  | FileUploadDragDropAreaMultipleProps;

const FileUploadDragDropArea = forwardRef(function FileUploadDragDropArea(
  props: FileUploadDragDropAreaProps,
  ref: React.ForwardedRef<HTMLInputElement>
) {
  const isMultiple = props.multiple;

  const {
    ariaLabel,
    name,
    caption = `Drag and drop the ${pluralize("file", isMultiple ? 2 : 1)} here`,
    className,
    textAlign = "center",
  } = props;

  const id = useId();
  const [isDragActivated, setIsDragActivated] = useState(false);

  const disableDragActivatedMode = () => {
    setIsDragActivated(false);
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActivated(true);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();

    const droppedFiles = Array.from(e.dataTransfer.files);

    requestAnimationFrame(() => {
      if (isMultiple) {
        if (droppedFiles.length > 0) {
          (props as FileUploadDragDropAreaMultipleProps).onChange(droppedFiles);
        }
      } else {
        const file = droppedFiles[0];
        if (file) {
          (props as FileUploadDragDropAreaSingleProps).onChange(file);
        }
      }
    });

    disableDragActivatedMode();
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    // Prevent default behavior (Prevent file from being opened)
    e.preventDefault();
    e.stopPropagation();
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    if (isMultiple) {
      const newFiles = Array.from(e.target.files || []);
      if (newFiles.length > 0) {
        (props as FileUploadDragDropAreaMultipleProps).onChange(newFiles);
      }
    } else {
      const file = e.target.files?.length && e.target.files[0];
      if (file) {
        (props as FileUploadDragDropAreaSingleProps).onChange(file);
      }
    }

    e.target.value = "";
  };

  return (
    <Box
      direction="column"
      align="center"
      justify="center"
      fullWidth
      className={cx(
        styles.dropArea,
        styles[textAlign],
        { [styles.dragActivated]: isDragActivated },
        className
      )}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragLeave={disableDragActivatedMode}
      onDragEnd={disableDragActivatedMode}
    >
      <input
        aria-label={ariaLabel || caption}
        name={name}
        type="file"
        id={id}
        ref={ref}
        onChange={handleInputChange}
        {...(isMultiple && { multiple: true })}
        {...withTestId(FILE_INPUT_TEST_ID)}
      />
      <Box
        direction={textAlign === "left" ? "row" : "column"}
        align={textAlign === "left" ? "start" : "center"}
        gap={textAlign === "left" ? "medium" : "0"}
      >
        <Icon src={CloudUpload} className={styles.fileUploadIcon} />
        <Icon src={DropFile} className={styles.onDropIcon} />
        <Box
          className={styles.fileUploadCaption}
          direction="column"
          align={textAlign === "left" ? "start" : "center"}
        >
          <Typography tag="p">{caption}</Typography>
          <Typography aria-hidden htmlFor={id} tag="label" className={styles.fileUploadLink}>
            or browse files
          </Typography>
        </Box>
        <Typography tag="span" className={styles.onDropCaption}>
          Drop the file here
        </Typography>
      </Box>
    </Box>
  );
});

FileUploadDragDropArea.displayName = "DS.FileUpload.DragDropArea";

export default FileUploadDragDropArea;
